import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { doc, getDoc, collection, query, where, getDocs, limit, updateDoc } from 'firebase/firestore';
import { db, collections } from '../../firebase.marketplace';
import LoadingSpinner from '../common/LoadingSpinner';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaStar, FaUser, FaCalendar, FaArrowLeft, FaShoppingBag, FaPlus, FaEdit, FaCheck, FaExclamationCircle } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../firebase.marketplace';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageSection = styled.div`
  position: relative;
  padding: 2rem;
`;

const MainImage = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: zoom-in;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ZoomedImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: transparent;
  color: white;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  z-index: 1001;
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 2rem;' : 'right: 2rem;'}
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Thumbnail = styled.div`
  width: 100%;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.active ? '#4a90e2' : 'transparent'};
  transition: all 0.3s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${props => props.active ? 1 : 0.6};
  }
  
  &:hover img {
    opacity: 1;
  }
`;

const InfoSection = styled.div`
  padding: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const Price = styled.div`
  color: #4a90e2;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const OriginalPrice = styled.span`
  color: #888;
  text-decoration: line-through;
  font-size: 1.2rem;
  font-weight: normal;
`;

const Discount = styled.span`
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const Description = styled.p`
  color: #888;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ProductTabs = styled.div`
  margin-bottom: 2rem;
`;

const TabButtons = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button`
  background: transparent;
  color: ${props => props.active ? 'white' : '#888'};
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.active ? '#4a90e2' : 'transparent'};
  }
`;

const TabContent = styled.div`
  color: #888;
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  
  &:before {
    content: '•';
    color: #4a90e2;
    margin-right: 0.75rem;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 8px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-size: 0.9rem;

  svg {
    color: #4a90e2;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const QuantityLabel = styled.span`
  color: #888;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
`;

const QuantityButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.span`
  color: white;
  padding: 0 1rem;
  min-width: 40px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AddToCartButton = styled(motion.button)`
  padding: 1rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: #357abd;
  }
  
  &:disabled {
    background: #888;
    cursor: not-allowed;
  }
`;

const BuyNowButton = styled(AddToCartButton)`
  background: transparent;
  border: 2px solid #4a90e2;

  &:hover {
    background: rgba(74, 144, 226, 0.1);
  }
`;

const ArtistInfo = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ArtistHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ArtistAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

const ArtistName = styled.h3`
  color: white;
  font-size: 1.2rem;
`;

const ArtistBio = styled.p`
  color: #888;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const RelatedProducts = styled.div`
  margin-top: 3rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
`;

const RelatedTitle = styled.h2`
  color: white;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: #4a90e2;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
`;

const RelatedCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const RelatedImage = styled.div`
  width: 100%;
  height: 180px;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const RelatedInfo = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RelatedProductName = styled.h4`
  color: white;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const RelatedPrice = styled.div`
  color: #4a90e2;
  font-weight: 600;
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const QuickAddButton = styled(motion.button)`
  background: rgba(74, 144, 226, 0.2);
  color: #4a90e2;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background: rgba(74, 144, 226, 0.4);
  }
`;

const NoRelatedProducts = styled.div`
  text-align: center;
  padding: 2rem;
  color: #888;
`;

const AddedMessage = styled(motion.div)`
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 0.75rem;
  border-radius: 5px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(244, 67, 54, 0.2);
  color: #F44336;
  padding: 0.75rem;
  border-radius: 5px;
  margin-top: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: white;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: #4a90e2;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 1.5rem;
  padding: 0.5rem 0;
`;

// Constants for the product detail page
const CURRENCY = 'UGX';

const ProductDetail = () => {
  const { productSlug } = useParams();
  // Extract the actual product ID from the slug (last part after the dash)
  const productId = productSlug.includes('-') 
    ? productSlug.split('-').pop() // Get the last part after the dash
    : productSlug;
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState(null);
  const [showZoom, setShowZoom] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [isOwner, setIsOwner] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState(null);
  const [userRoles, setUserRoles] = useState({});

  // Check if user has artist or admin role
  useEffect(() => {
    if (currentUser) {
      // Get user roles from localStorage as a fallback for permission issues
      try {
        const storedRoles = localStorage.getItem('konzo_user_roles');
        if (storedRoles) {
          const roles = JSON.parse(storedRoles);
          setUserRoles(roles);
        }
      } catch (error) {
        console.error('Error getting stored roles:', error);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch product
        const productDoc = await getDoc(doc(db, collections.PRODUCTS, productId));
        
        if (!productDoc.exists()) {
          setError('Product not found');
          setLoading(false);
          return;
        }
        
        const productData = {
          $id: productDoc.id,
          ...productDoc.data()
        };
        
        setProduct(productData);
        setEditedProduct(productData);
        
        console.log('Current user:', currentUser?.uid);
        console.log('Product seller:', productData.sellerId);
        
        // Check if current user is the product owner or an admin
        if (currentUser) {
          const isProductOwner = productData.sellerId === currentUser.uid;
          const isAdmin = userRoles[ROLES.ADMIN] || userRoles[ROLES.SUPERADMIN];
          
          if (isProductOwner || isAdmin) {
            console.log('User can edit this product');
            setIsOwner(true);
          } else {
            console.log('User cannot edit this product');
            setIsOwner(false);
          }
        } else {
          setIsOwner(false);
        }
        
        // Fetch seller info
        if (productData.sellerId) {
          const sellerDoc = await getDoc(doc(db, collections.USERS, productData.sellerId));
          if (sellerDoc.exists()) {
            setSeller({
              $id: sellerDoc.id,
              ...sellerDoc.data()
            });
          }
        }

        
        // Fetch related products (same category or from same seller)
        const relatedQuery = query(
          collection(db, collections.PRODUCTS),
          where('status', '==', 'active'),
          where('sellerId', '==', productData.sellerId),
          limit(4)
        );
        
        const relatedSnapshot = await getDocs(relatedQuery);
        const relatedItems = relatedSnapshot.docs
          .map(doc => ({
            $id: doc.id,
            ...doc.data()
          }))
          .filter(item => item.$id !== productId); // Exclude current product
        
        setRelatedProducts(relatedItems);
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId, currentUser]);

  const handleQuantityChange = (change) => {
    setQuantity(prev => Math.max(1, prev + change));
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    addToCart({
      id: product.$id,
      title: product.name,
      price: product.price,
      imageUrl: product.imageUrl,
      type: 'product',
      sellerId: product.sellerId,
      quantity: quantity
    });
    
    setAddedToCart(true);
    setTimeout(() => {
      setAddedToCart(false);
    }, 3000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/marketplace/checkout');
  };

  const handleGoBack = () => {
    navigate('/marketplace');
  };
  
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      // Starting to edit - initialize editedProduct with current product data
      setEditedProduct({...product});
    }
  };
  
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) : value
    }));
  };
  
  const handleSaveChanges = async () => {
    try {
      console.log('Saving product changes:', editedProduct);
      
      // Create updated product data
      const updatedData = {
        name: editedProduct.name,
        description: editedProduct.description,
        price: parseFloat(editedProduct.price),
        stock: parseInt(editedProduct.stock) || 0,
        updatedAt: new Date()
      };
      
      try {
        // Try to update the product in Firestore
        await updateDoc(doc(db, collections.PRODUCTS, productId), updatedData);
        console.log('Product updated in Firestore');
      } catch (firestoreError) {
        // If Firestore update fails due to permissions, update locally only
        console.warn('Could not update product in Firestore:', firestoreError);
        console.log('Updating product locally only');
      }
      
      // Update local state regardless of Firestore success
      const updatedProduct = {
        ...product,
        ...updatedData
      };
      
      setProduct(updatedProduct);
      setIsEditing(false);
      toast.success('Product updated successfully!');
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product. Please try again.');
    }
  };

  const handleImageClick = () => {
    setShowZoom(true);
  };
  
  const handleCloseZoom = () => {
    setShowZoom(false);
  };
  
  const handlePrevImage = () => {
    setSelectedImage(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };
  
  const handleNextImage = () => {
    setSelectedImage(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const calculateDiscount = () => {
    if (!product.originalPrice || !product.price) return null;
    
    const originalPrice = parseFloat(product.originalPrice);
    const currentPrice = parseFloat(product.price);
    
    if (originalPrice <= currentPrice) return null;
    
    const discount = Math.round((1 - currentPrice / originalPrice) * 100);
    return discount > 0 ? discount : null;
  };

  const handleQuickAdd = (e, relatedProduct) => {
    e.stopPropagation(); // Prevent navigation to product detail
    
    addToCart({
      id: relatedProduct.$id,
      title: relatedProduct.name,
      price: relatedProduct.price,
      imageUrl: relatedProduct.imageUrl,
      type: 'product',
      sellerId: relatedProduct.sellerId,
      quantity: 1
    });
    
    toast.success(`${relatedProduct.name} added to cart`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container>
        <BackButton onClick={handleGoBack}>
          <FaArrowLeft /> Back to Marketplace
        </BackButton>
        <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
          {error || 'Product not found'}
        </div>
      </Container>
    );
  }

  // Handle missing images
  const images = product?.images?.length ? product.images : [];
  if (product?.imageUrl && !images.includes(product.imageUrl)) {
    images.unshift(product.imageUrl);
  }
  
  // Ensure we always have at least one image
  if (images.length === 0) {
    images.push('/placeholder-product.jpg');
  }

  return (
    <Container>
      <BackButton onClick={handleGoBack} whileHover={{ x: -5 }}>
        <FaArrowLeft /> Back to Marketplace
      </BackButton>
      
      <ProductGrid>
        <ImageSection>
          <MainImage onClick={handleImageClick}>
            <img 
              src={images[selectedImage]} 
              alt={product.name} 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-product.jpg';
              }}
            />
          </MainImage>
          
          {images.length > 1 && (
            <ThumbnailGrid>
              {images.map((image, index) => (
                <Thumbnail
                  key={index}
                  active={selectedImage === index}
                  onClick={() => setSelectedImage(index)}
                >
                  <img
                    src={image}
                    alt={`${product.name} thumbnail ${index + 1}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                </Thumbnail>
              ))}
            </ThumbnailGrid>
          )}
          
          {showZoom && (
            <ImageOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <CloseButton onClick={handleCloseZoom}>×</CloseButton>
              <ImageNavButton direction="left" onClick={handlePrevImage}>
                ←
              </ImageNavButton>
              <ZoomedImage 
                src={images[selectedImage]} 
                alt={product.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/placeholder-product.jpg';
                }}
              />
              <ImageNavButton direction="right" onClick={handleNextImage}>
                →
              </ImageNavButton>
            </ImageOverlay>
          )}
        </ImageSection>

        <InfoSection>
          {isEditing ? (
            <div style={{ marginBottom: '1rem' }}>
              <Label>Product Name</Label>
              <Input 
                type="text" 
                name="name" 
                value={editedProduct.name} 
                onChange={handleEditChange} 
                style={{ marginBottom: '1rem' }}
              />
              <Label>Price (UGX)</Label>
              <Input 
                type="number" 
                name="price" 
                value={editedProduct.price} 
                onChange={handleEditChange} 
                style={{ marginBottom: '1rem' }}
              />
              <Label>Stock Available</Label>
              <Input 
                type="number" 
                name="stock" 
                value={editedProduct.stock || 0} 
                onChange={handleEditChange} 
                style={{ marginBottom: '1rem' }}
              />
            </div>
          ) : (
            <>
              <Title>{product.name}</Title>
              
              <Price>
                {parseFloat(product.price).toLocaleString('en-UG')} {CURRENCY}
                {product.originalPrice && parseFloat(product.originalPrice) > parseFloat(product.price) && (
                  <>
                    <OriginalPrice>{parseFloat(product.originalPrice).toLocaleString('en-UG')} {CURRENCY}</OriginalPrice>
                    <Discount>{calculateDiscount()}% OFF</Discount>
                  </>
                )}
              </Price>
            </>
          )}
          
          <Stats>
            {product.rating && (
              <StatItem>
                <FaStar />
                <span>{product.rating} ({product.reviewCount || 0} reviews)</span>
              </StatItem>
            )}
            {product.salesCount && (
              <StatItem>
                <FaUser />
                <span>{product.salesCount} sold</span>
              </StatItem>
            )}
            {product.createdAt && (
              <StatItem>
                <FaCalendar />
                <span>Listed {new Date(product.createdAt.seconds * 1000).toLocaleDateString()}</span>
              </StatItem>
            )}
          </Stats>
          
          <ProductTabs>
            <TabButtons>
              <TabButton 
                active={activeTab === 'description'} 
                onClick={() => setActiveTab('description')}
              >
                Description
              </TabButton>
              <TabButton 
                active={activeTab === 'features'} 
                onClick={() => setActiveTab('features')}
              >
                Features
              </TabButton>
              <TabButton 
                active={activeTab === 'details'} 
                onClick={() => setActiveTab('details')}
              >
                Details
              </TabButton>
            </TabButtons>
            
            <TabContent>
              {activeTab === 'description' && (
                isEditing ? (
                  <>
                    <Label>Product Description</Label>
                    <TextArea 
                      name="description" 
                      value={editedProduct.description} 
                      onChange={handleEditChange}
                      rows={6}
                    />
                  </>
                ) : (
                  <Description>{product.description}</Description>
                )
              )}
              
              {activeTab === 'features' && (
                <FeatureList>
                  {product.features ? (
                    product.features.map((feature, index) => (
                      <FeatureItem key={index}>{feature}</FeatureItem>
                    ))
                  ) : (
                    <p>No features listed for this product.</p>
                  )}
                </FeatureList>
              )}
              
              {activeTab === 'details' && (
                <div>
                  {product.details ? (
                    Object.entries(product.details).map(([key, value]) => (
                      <p key={key}><strong>{key}:</strong> {value}</p>
                    ))
                  ) : (
                    <p>No additional details available for this product.</p>
                  )}
                </div>
              )}
            </TabContent>
          </ProductTabs>

          {product.stock > 0 && (
            <QuantitySelector>
              <QuantityLabel>Quantity:</QuantityLabel>
              <QuantityControls>
                <QuantityButton 
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  -
                </QuantityButton>
                <QuantityValue>{quantity}</QuantityValue>
                <QuantityButton 
                  onClick={() => handleQuantityChange(1)}
                  disabled={product.stock && quantity >= product.stock}
                >
                  +
                </QuantityButton>
              </QuantityControls>
              <span style={{ color: '#888', marginLeft: 'auto', fontSize: '0.9rem' }}>
                {product.stock} available
              </span>
            </QuantitySelector>
          )}
          
          {product.stock === 0 && (
            <div style={{ 
              background: 'rgba(244, 67, 54, 0.1)', 
              color: '#F44336', 
              padding: '0.75rem', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <FaExclamationCircle /> This product is currently out of stock
            </div>
          )}

          {isOwner && (
            <div style={{ marginBottom: '1rem' }}>
              <AddToCartButton
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(`/marketplace/edit-product/${productId}`)}
                style={{ background: '#4a90e2', marginBottom: '1rem' }}
              >
                <FaEdit /> Edit Product
              </AddToCartButton>
            </div>
          )}
          

          
          {!isEditing && (
            <ButtonsContainer>
              {product.stock === 0 ? (
                <AddToCartButton
                  style={{ background: '#888', cursor: 'not-allowed' }}
                >
                  <FaShoppingCart /> Out of Stock
                </AddToCartButton>
              ) : (
                <AddToCartButton
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                >
                  <FaShoppingCart /> Add to Cart
                </AddToCartButton>
              )}

              {product.stock === 0 ? (
                <BuyNowButton
                  style={{ background: 'transparent', borderColor: '#888', color: '#888', cursor: 'not-allowed' }}
                >
                  Currently Unavailable
                </BuyNowButton>
              ) : (
                <BuyNowButton
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleBuyNow}
                >
                  Buy Now
                </BuyNowButton>
              )}
            </ButtonsContainer>
          )}
          
          {addedToCart && (
            <AddedMessage
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <FaShoppingCart /> Added to cart successfully!
            </AddedMessage>
          )}

          {seller && (
            <ArtistInfo>
              <ArtistHeader>
                <ArtistAvatar 
                  src={seller.photoURL || '/placeholder-avatar.jpg'} 
                  alt={seller.displayName || 'Seller'} 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-avatar.jpg';
                  }}
                />
                <ArtistName>{seller.displayName || seller.email || 'Seller'}</ArtistName>
              </ArtistHeader>
              <ArtistBio>{seller.bio || 'No seller information available.'}</ArtistBio>
            </ArtistInfo>
          )}
        </InfoSection>
      </ProductGrid>

      {relatedProducts.length > 0 && (
        <RelatedProducts>
          <RelatedTitle>
            <FaShoppingBag /> More from this Seller
          </RelatedTitle>
          <RelatedGrid>
            {relatedProducts.map(relatedProduct => (
              <RelatedCard
                key={relatedProduct.$id}
                onClick={() => navigate(`/marketplace/products/${relatedProduct.$id}`)}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <RelatedImage>
                  <img 
                    src={relatedProduct.imageUrl || '/placeholder-product.jpg'} 
                    alt={relatedProduct.name} 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                </RelatedImage>
                <RelatedInfo>
                  <RelatedProductName>{relatedProduct.name}</RelatedProductName>
                  <RelatedPrice>
                    {parseFloat(relatedProduct.price).toLocaleString('en-UG')} {CURRENCY}
                    <QuickAddButton
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleQuickAdd(e, relatedProduct)}
                    >
                      <FaPlus />
                    </QuickAddButton>
                  </RelatedPrice>
                </RelatedInfo>
              </RelatedCard>
            ))}
          </RelatedGrid>
        </RelatedProducts>
      )}
      
      {relatedProducts.length === 0 && !loading && !error && (
        <RelatedProducts>
          <RelatedTitle>
            <FaShoppingBag /> More Products
          </RelatedTitle>
          <NoRelatedProducts>
            No related products found.
          </NoRelatedProducts>
        </RelatedProducts>
      )}
    </Container>
  );
};

export default ProductDetail;
